import { CatalogResponseSchema, type Module } from '@/shared/schemas';
import type { CatalogSource } from '@/shared/types';
import { STATIC_CATALOG } from '../domain/static-catalog';

const API_URL = '/api/catalog';
const API_TIMEOUT_MS = 3000;

export interface CatalogResult {
  readonly modules: readonly Module[];
  readonly source: CatalogSource;
  readonly fetchedAt: string;
}

/**
 * Fetch module catalog from API with static fallback.
 *
 * Strategy:
 * 1. Try Rust catalog API
 * 2. If it fails or times out, load static catalog
 */
export async function fetchCatalog(): Promise<CatalogResult> {
  try {
    const modules = await fetchFromApi();
    return {
      modules,
      source: 'api',
      fetchedAt: new Date().toISOString(),
    };
  } catch {
    console.info('[catalog] API unavailable, using static fallback');
    return {
      modules: STATIC_CATALOG,
      source: 'static',
      fetchedAt: new Date().toISOString(),
    };
  }
}

async function fetchFromApi(): Promise<readonly Module[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    const response = await fetch(API_URL, {
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data: unknown = await response.json();
    const parsed = CatalogResponseSchema.safeParse(data);

    if (!parsed.success) {
      console.error('[catalog] Validation failed:', parsed.error.flatten());
      throw new Error('Invalid catalog response');
    }

    return parsed.data.modules;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Log catalog source for debugging.
 */
export function logCatalogSource(source: CatalogSource): void {
  if (source === 'static') {
    console.info('[catalog] Loaded from static fallback');
  } else {
    console.info('[catalog] Loaded from API');
  }
}
